import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Users, School, ChefHat, Building2, Landmark, Zap, TrendingUp, Shield, Clock, DollarSign, MapPin, HeartHandshake, Gift, Rocket, Heart, Award, Play, SkipForward, RotateCcw } from 'lucide-react';
import Header from '../components/Header';
import '../styles/index.css';

const Landing = () => {
  const navigate = useNavigate();
  const [showIntro, setShowIntro] = useState(true);
  const [introEnded, setIntroEnded] = useState(false);
  const introRef = useRef(null);
  const sectionVideoRef = useRef(null);

  useEffect(() => { document.title = "EcoLunch - Plateforme Québécoise de gestion des repas scolaires"; }, []);

  const skipIntro = () => {
    setShowIntro(false);
    if (introRef.current) introRef.current.pause();
  };

  const replayIntro = () => {
    setShowIntro(true);
    setIntroEnded(false);
    if (introRef.current) {
      introRef.current.currentTime = 0;
      introRef.current.play();
    }
  };

  return (
    <div className="home-container">
      {/* Video Intro Overlay */}
      {showIntro && !introEnded && (
        <div className="video-intro-overlay" data-testid="video-intro">
          <video
            ref={introRef}
            autoPlay
            muted
            playsInline
            onEnded={() => { setIntroEnded(true); setShowIntro(false); }}
            className="video-intro-player"
          >
            <source src="/intro-ecolunch.mp4" type="video/mp4" />
          </video>
          <button className="video-intro-skip" onClick={skipIntro} data-testid="skip-intro-btn">
            <SkipForward size={16} /> Passer l'intro
          </button>
        </div>
      )}

      <Header currentPage="home" lang="fr" />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge animate-fade-in-up">
            <Zap size={16} />
            <span>Plateforme de gestion unifiée - Innovation Québécoise</span>
          </div>
          <div className="hero-logo-title animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <img src={require('../assets/logo-ecolunch-white.png')} alt="EcoLunch" className="hero-main-logo" />
          </div>
          <h1 className="hero-title animate-fade-in-up" style={{ animationDelay: '0.15s', color: '#FFFFFF', textShadow: '0 0 10px rgba(255,255,255,0.8), 0 0 30px rgba(132,204,22,0.5), 0 0 60px rgba(132,204,22,0.3)' }}>
            La plateforme Québécoise de gestion des repas scolaires
          </h1>
          <p className="hero-subtitle animate-fade-in-up" style={{ animationDelay: '0.2s', fontSize: '1.15rem', maxWidth: '700px', margin: '0 auto' }}>
            Commandez, pilotez, automatisez et supervisez vos repas scolaires — le tout en un seul endroit.
          </p>
        </div>
      </section>

      {/* Video Présentation */}
      <section className="section" style={{paddingTop: '2rem', paddingBottom: '2rem'}}>
        <div className="section-container">
          <div className="video-section" data-testid="video-presentation">
            <video
              ref={sectionVideoRef}
              controls
              playsInline
              className="video-presentation-player"
            >
              <source src="/presentation-ecolunch.mp4" type="video/mp4" />
            </video>
          </div>
          {introEnded || !showIntro ? (
            <div style={{textAlign: 'center', marginTop: '1rem'}}>
              <button className="video-replay-btn" onClick={replayIntro} data-testid="replay-intro-btn">
                <RotateCcw size={14} /> Revoir l'intro
              </button>
            </div>
          ) : null}
        </div>
      </section>

      {/* Problem vs Solution */}
      <section id="solution" className="section is-not-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Comprendre EcoLunch et son Écosystème unifié</h2>
            <p className="section-subtitle">
              La différence entre un écosystème unifié et des solutions fragmentées
            </p>
          </div>

          <div className="is-not-grid">
            <div className="is-not-card not-card">
              <div className="is-not-icon">❌</div>
              <h3>Ce que nous ne sommes PAS</h3>
              <ul>
                <li>Un "Uber Eats" pour écoles</li>
                <li>Un marketplace de traiteurs</li>
                <li>Des sites web séparés</li>
                <li>Une simple commande en ligne</li>
                <li>Des intégrations tierces fragiles</li>
              </ul>
            </div>

            <div className="is-not-card is-card">
              <div className="is-not-icon">✅</div>
              <h3>Ce que nous SOMMES</h3>
              <ul>
                <li><strong>UN système ERP/SaaS centralisé</strong></li>
                <li><strong>CINQ portails en UNE plateforme</strong></li>
                <li>Architecture hiérarchique à 5 niveaux</li>
                <li>Données centralisées et sécurisées</li>
                <li>Flux d'informations orchestrés en temps réel</li>
                <li>Automatisation de bout en bout</li>
              </ul>
            </div>
          </div>

          <div className="solution-explainer">
            <p>
              <strong>En clair :</strong> EcoLunch n'est pas un assemblage de sites web. 
              C'est une plateforme unique où Parents, Écoles, Traiteurs et Directions 
              travaillent dans un écosystème connecté et automatisé.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Actors Overview */}
      <section id="acteurs" className="section actors-overview-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Qui utilise EcoLunch ?</h2>
            <p className="section-subtitle">
              5 types d'utilisateurs, 5 besoins uniques, 5 portails dédiés
            </p>
          </div>

          <div className="actors-grid-simple">
            <div className="actor-card-simple" onClick={() => navigate('/parents')}>
              <div className="actor-icon-simple parent-color">
                <Users size={40} />
              </div>
              <h3>Parents</h3>
              <p>Commandez les repas de tous vos enfants en 15 secondes</p>
            </div>

            <div className="actor-card-simple" onClick={() => navigate('/caterers')}>
              <div className="actor-icon-simple caterer-color">
                <ChefHat size={40} />
              </div>
              <h3>Traiteurs</h3>
              <p>Automatisez votre back-office et augmentez vos revenus de manière substantielle</p>
            </div>

            <div className="actor-card-simple" onClick={() => navigate('/schools')}>
              <div className="actor-icon-simple school-color">
                <School size={40} />
              </div>
              <h3>Écoles</h3>
              <p>Dashboard temps réel pour piloter la satisfaction alimentaire</p>
            </div>

            <div className="actor-card-simple" onClick={() => navigate('/directions')}>
              <div className="actor-icon-simple direction-color">
                <Building2 size={40} />
              </div>
              <h3>Directions</h3>
              <p>Vue consolidée multi-établissements pour la gouvernance</p>
            </div>

            <div className="actor-card-simple" onClick={() => navigate('/css')}>
              <div className="actor-icon-simple css-color">
                <Landmark size={40} />
              </div>
              <h3>CSS (siège)</h3>
              <p>Gouvernance alimentaire globale et politique stratégique</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pourquoi EcoLunch */}
      <section className="section" data-testid="pourquoi-ecolunch">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Pourquoi EcoLunch ?</h2>
            <p className="section-subtitle">
              La première infrastructure SaaS verticale conçue au Québec, pour les réalités du Québec
            </p>
          </div>

          <div className="pourquoi-grid">
            <div className="pourquoi-tile" data-testid="tile-quebec">
              <div className="portal-icon" style={{color: '#84CC16'}}>
                <MapPin size={30} />
              </div>
              <h3>Conçu au Québec, pour le Québec</h3>
              <p>
                Pensé et déployé par des Québécois qui connaissent directement la réalité des écoles, des parents et des traiteurs d'ici. 
                Pas un outil générique adapté à la hâte — une plateforme bâtie selon nos modes de fonctionnement, nos contraintes terrain et nos réalités administratives.
              </p>
            </div>

            <div className="pourquoi-tile" data-testid="tile-fondateurs">
              <div className="portal-icon" style={{color: '#FF8C00'}}>
                <ChefHat size={30} />
              </div>
              <h3>Gratuit pour les Traiteurs (&lt;20 écoles)</h3>
              <p>
                EcoLunch est <strong>entièrement gratuit pour tout traiteur desservant moins de 20 écoles</strong>. 
                Aucun abonnement, aucun frais de support, aucun frais de mise à jour. 
                Accès complet à toutes les fonctionnalités, mises à jour et support technique inclus.
              </p>
            </div>

            <div className="pourquoi-tile" data-testid="tile-gratuit">
              <div className="portal-icon" style={{color: '#F472B6'}}>
                <Gift size={30} />
              </div>
              <h3>Gratuit pour les Écoles et les Parents</h3>
              <p>
                EcoLunch sera et restera gratuit pour les écoles — avec possibilité de versement de dons. 
                Pour les parents, l'utilisation est également gratuite. L'accès au service doit être simple, fluide et sans barrières.
              </p>
            </div>

            <div className="pourquoi-tile" data-testid="tile-connect">
              <div className="portal-icon" style={{color: '#22C55E'}}>
                <Rocket size={30} />
              </div>
              <h3>EcoLunch Connect — Moteur de croissance</h3>
              <p>
                Nous travaillons avec des écoles qui souhaitent offrir un service de repas chauds mais ne le font pas encore. 
                En éliminant la complexité opérationnelle, nous aidons les écoles à démarrer et les traiteurs à élargir leur réseau.
              </p>
            </div>

            <div className="pourquoi-tile" data-testid="tile-coparentalite">
              <div className="portal-icon" style={{color: '#00D4FF'}}>
                <HeartHandshake size={30} />
              </div>
              <h3>Coparentalité intégrée</h3>
              <p>
                La réalité des familles n'est pas uniforme. EcoLunch intègre un portail de coparentalité pour gérer les responsabilités 
                de commande, paiement et suivi entre plusieurs adultes. Une plateforme enracinée dans la réalité d'ici.
              </p>
            </div>

            <div className="pourquoi-tile" data-testid="tile-partenaires">
              <div className="portal-icon" style={{color: '#FACC15'}}>
                <Award size={30} />
              </div>
              <h3>Fondation locale et crédible</h3>
              <p>
                <strong>Sezzle Canada</strong> — paiements flexibles<br />
                <strong>Paysafe Montréal</strong> — traitement sécurisé<br />
                <strong>OVH Cloud Québec</strong> — hébergement souverain<br /><br />
                Des partenaires reconnus pour une infrastructure solide.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Site Web */}
      <section className="section" style={{paddingTop: '2rem', paddingBottom: '2rem'}}>
        <div className="section-container" style={{textAlign: 'center'}}>
          <a href="https://ecotechsysteme.ca" target="_blank" rel="noopener noreferrer" style={{color: 'var(--color-lime)', fontSize: '1.2rem', fontWeight: 600, textDecoration: 'none', borderBottom: '2px solid rgba(132,204,22,0.3)', paddingBottom: '0.25rem'}}>
            ecotechsysteme.ca
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/logo-ecolunch.png" alt="EcoLunch" className="footer-logo-image" />
              </div>
              <p className="footer-tagline">
                La plateforme unifiée pour moderniser les repas scolaires au Québec
              </p>
              <div className="footer-badge-100">
                <span>🇨🇦</span>
                <div>
                  <strong>100% Québec</strong>
                  <p style={{fontSize: '0.85rem', marginTop: '0.3rem', color: 'var(--color-text-secondary)'}}>
                    Hébergement, Paiements, Données, Conception, Soutien
                  </p>
                </div>
              </div>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4>Solutions</h4>
                <a onClick={() => navigate('/caterers')}>Pour les Traiteurs</a>
                <a onClick={() => navigate('/parents')}>Pour les Parents</a>
                <a onClick={() => navigate('/schools')}>Pour les Écoles</a>
                <a onClick={() => navigate('/directions')}>Pour les Directions</a>
                <a onClick={() => navigate('/css')}>Pour les CSS</a>
              </div>

              <div className="footer-column">
                <h4>Plateforme</h4>
                <a onClick={() => navigate('/platform')}>Architecture</a>
                <a onClick={() => navigate('/platform')}>Sécurité</a>
                <a onClick={() => navigate('/coparentalite')}>Co-Parentalité</a>
                <a onClick={() => navigate('/ecopulse')}>EcoPulse CC</a>
                <a onClick={() => navigate('/ecoloop')}>EcoLoop</a>
              </div>

              <div className="footer-column">
                <h4>Entreprise</h4>
                <a href="#">À propos</a>
                <a onClick={() => navigate('/contact')}>Contact</a>
                <a href="#">Carrières</a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="footer-legal">
              <span>© 2025 EcoLunch. Tous droits réservés.</span>
              <div className="footer-legal-links">
                <a href="#">Confidentialité</a>
                <a href="#">Conditions</a>
                <a href="#">Sécurité</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
